/* Shared behaviour for the Hooman blocks.
   Loaded by whichever block needs it; the guard below makes a second <script> harmless. */
(function () {
  if (window.__hoomanInit) return;
  window.__hoomanInit = true;

  /* In-page anchors.

     Horizon scrolls a wrapper element rather than the document -- `html` and `body` are
     `overflow: hidden` at viewport height -- so a plain href="#sizes" moves nothing: the
     browser scrolls the document, and the document does not scroll. Delegating on the
     document and calling scrollIntoView() lets the browser find whichever ancestor
     actually scrolls, which is the one thing it is reliably good at.

     The hash is still written to the URL, so the link remains shareable and Back works. */
  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('a[href^="#"]');
    if (!link) return;

    var hash = link.getAttribute('href');
    if (!hash || hash === '#') return;

    var target = document.getElementById(hash.slice(1));
    if (!target) return;

    event.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (history.replaceState) {
      history.replaceState(null, '', hash);
    }
  });

  /* The same problem on first load: arriving at /?x#sizes leaves the page at the top,
     because the anchor is resolved before the section has laid out. One frame later it
     has, and the browser can be asked again. */
  if (location.hash.length > 1) {
    requestAnimationFrame(function () {
      var target = document.getElementById(location.hash.slice(1));
      if (target) target.scrollIntoView({ block: 'start' });
    });
  }
})();

/* Click anywhere on the page to close the cart drawer.

   Horizon has a handler for this but its own comment says it is "only meaningful in modal
   mode — in sidebar mode the dialog has no backdrop so this handler is inert". The cart
   drawer is a sidebar, so nothing ever dismissed it but its close button. Now that it
   floats over the page rather than pushing it aside, clicking the page is the obvious way
   to put it away.

   Done with an invisible scrim rather than a document click listener, and that is the
   whole point of the approach. A listener cannot reliably swallow the click: Horizon
   binds its `on:click="..."` attributes through its own delegated handler on the document,
   registered before this file runs, so stopPropagation cannot beat it — clicking the
   product photo to dismiss the cart also opened the image zoom. A scrim takes the click
   itself, so nothing underneath ever sees it.

   The scrim sits above the header and below the drawer, so the drawer stays interactive
   and everything else is a dismiss target. It is transparent: no dimming was asked for,
   and adding it would be a visual decision rather than a behavioural one. */
(function () {
  var CLASS = 'hooman-drawer-scrim';
  var scrim = null;

  function drawer() {
    return document.getElementById('cart-drawer');
  }

  function show() {
    if (scrim) return;
    scrim = document.createElement('div');
    scrim.className = CLASS;
    scrim.addEventListener('click', function () {
      var d = drawer();
      if (d && typeof d.close === 'function') d.close();
    });
    document.body.appendChild(scrim);
  }

  function hide() {
    if (!scrim) return;
    scrim.remove();
    scrim = null;
  }

  function sync() {
    var d = drawer();
    if (d && d.hasAttribute('open')) show();
    else hide();
  }

  function watch() {
    var d = drawer();
    if (!d || d.dataset.hoomanScrim) return;
    d.dataset.hoomanScrim = '1';
    sync();
    new MutationObserver(sync).observe(d, { attributes: true, attributeFilter: ['open'] });
  }

  watch();
  /* The drawer is replaced wholesale when the cart changes, so pick up the new one. */
  if (typeof MutationObserver === 'function') {
    new MutationObserver(function () {
      watch();
      sync();
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();

/* ---- Bed line-up: swipe carousel on mobile, dots under the beds on desktop ----
   Chris, 2026-09-22.

   Mobile: one bed centred at full size, its neighbours 20% smaller and bleeding off both
   edges, a single name underneath that changes as you swipe, colour dots that slide in
   step so the centred dot is the centred bed, and a loop that never ends.

   Desktop: the beds stay in a row and each one carries its own dot and name underneath,
   so the swatch row in the copy column is not needed.

   The loop is three copies of the line-up with the real blocks in the middle one. When
   the rail comes to rest outside that middle copy it is shifted by exactly one copy's
   width -- the content either side is identical, so nothing moves on screen and there is
   no end to reach. The real blocks stay in the middle copy so the theme editor still
   highlights the right thing when a colourway is selected.

   The dots are matched to the beds BY NAME, not by position: the beds are child blocks
   and the dots are a "Name | #hex" textarea, and on this page the two were authored in
   different orders. If a name fails to match, the dots are left alone entirely rather
   than half-sorted. */
(function () {
  var MOBILE = '(max-width: 899px)';
  var MIN_SCALE = 0.8; /* "20% smaller", from the brief */
  var SETS = 3; /* one copy either side of the visible one */
  var SETTLE = 150; /* ms of stillness before the rail is quietly re-based */

  function slice(list) {
    return Array.prototype.slice.call(list);
  }

  function text(el) {
    return el ? el.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  /* A copy is scenery: no name to a screen reader, and nothing in it can be tabbed to. */
  function asCopy(node) {
    node.classList.add('hooman-bed__clone');
    node.setAttribute('aria-hidden', 'true');
    var reachable = node.querySelectorAll('a, button, input, select, textarea, [tabindex]');
    for (var i = 0; i < reachable.length; i++) reachable[i].setAttribute('tabindex', '-1');
    return node;
  }

  function Carousel(rail) {
    this.rail = rail;
    this.origins = slice(rail.querySelectorAll('.hooman-bed__colour'));
    this.n = this.origins.length;
    if (this.n < 1) return;

    var band = rail.closest('.hooman-bed');
    this.band = band;
    this.name = band.querySelector('.hooman-bed__nowshowing');
    this.dotWrap = band.querySelector('.hooman-bed__swatches');
    this.track = band.querySelector('.hooman-bed__swatchtrack');
    this.authored = this.track ? slice(this.track.querySelectorAll('.hooman-bed__swatch')) : [];

    this.labels = this.origins.map(function (slide) {
      return text(slide.querySelector('.hooman-bed__captiontext'));
    });
    this.paired = this.pair();

    /* Nothing is hidden until the script is known to be running: without it the beds keep
       their own captions and the dots keep their labels, which is what the band looked
       like before any of this. */
    band.classList.add('hooman-bed--carousel-live');

    this.active = -1;
    this.ticking = false;
    this.mode = null;
    this.bind();
    this.layout();
  }

  /* The dots in the order the beds are in. Null if any name fails to match. */
  Carousel.prototype.pair = function () {
    if (this.authored.length !== this.n) return null;
    var pool = this.authored.slice();
    var out = [];
    for (var i = 0; i < this.n; i++) {
      var want = this.labels[i].toLowerCase();
      var hit = -1;
      for (var j = 0; j < pool.length; j++) {
        if (text(pool[j]).toLowerCase() === want) {
          hit = j;
          break;
        }
      }
      if (hit === -1) return null;
      out.push(pool.splice(hit, 1)[0]);
    }
    return out;
  };

  /* ---- building --------------------------------------------------------- */

  Carousel.prototype.strip = function () {
    var copies = this.band.querySelectorAll('.hooman-bed__clone');
    for (var i = 0; i < copies.length; i++) copies[i].parentNode.removeChild(copies[i]);
    if (!this.track) return;
    for (var j = 0; j < this.authored.length; j++) this.track.appendChild(this.authored[j]);
  };

  Carousel.prototype.layout = function () {
    /* One colourway is a row at every width -- there is nothing to swipe between, but the
       dot still belongs under the bed rather than up in the copy. */
    var want = this.n > 1 && window.matchMedia(MOBILE).matches ? 'swipe' : 'row';
    if (this.mode === want) return;
    this.mode = want;

    this.strip();
    if (want === 'swipe') this.buildSwipe();
    else this.buildRow();

    this.active = -1;
    this.render();
  };

  /* Desktop: the authored line-up, each bed carrying its own dot and name. */
  Carousel.prototype.buildRow = function () {
    this.slides = this.origins.slice();
    for (var i = 0; i < this.slides.length; i++) this.rail.appendChild(this.slides[i]);

    this.dots = [];
    if (!this.paired) {
      this.band.classList.remove('hooman-bed--dots-under');
      return;
    }
    for (var d = 0; d < this.n; d++) {
      var caption = this.origins[d].querySelector('.hooman-bed__caption');
      if (!caption) continue;
      /* Up here the dot is a label, not a control -- the bed it names is already on
         screen -- so it is readable but not something to tab to and press. */
      this.paired[d].setAttribute('tabindex', '-1');
      caption.appendChild(this.paired[d]);
    }
    this.band.classList.add('hooman-bed--dots-under');
  };

  /* Mobile: three copies of the line-up, and the dots back in their sliding strip. */
  Carousel.prototype.buildSwipe = function () {
    this.band.classList.remove('hooman-bed--dots-under');

    this.slides = [];
    this.dots = [];
    var beds = document.createDocumentFragment();
    var strip = document.createDocumentFragment();

    for (var s = 0; s < SETS; s++) {
      for (var i = 0; i < this.n; i++) {
        var real = s === 1;
        var bed = real ? this.origins[i] : asCopy(this.origins[i].cloneNode(true));
        beds.appendChild(bed);
        this.slides.push(bed);

        if (!this.paired) continue;
        var dot = real ? this.paired[i] : asCopy(this.paired[i].cloneNode(true));
        if (real) dot.removeAttribute('tabindex');
        strip.appendChild(dot);
        this.dots.push(dot);
      }
    }

    this.rail.appendChild(beds);
    if (this.track) this.track.appendChild(strip);

    var self = this;
    for (var d = 0; d < this.dots.length; d++) {
      (function (index) {
        self.dots[index].addEventListener('click', function () {
          self.goTo(index);
        });
      })(d);
    }

    /* Open on the middle bed of the middle copy, which is the arrangement the mock-up
       shows and also leaves a whole copy of slack in both directions. */
    var step = this.step();
    if (step) this.rail.scrollLeft = step * (this.n + Math.floor(this.n / 2));
  };

  /* ---- position --------------------------------------------------------- */

  Carousel.prototype.step = function () {
    if (!this.slides || this.slides.length < 2) return 0;
    return this.slides[1].offsetLeft - this.slides[0].offsetLeft;
  };

  /* Where the rail is, measured in slides: 1.5 is halfway between the second and third. */
  Carousel.prototype.progress = function () {
    var step = this.step();
    return step ? this.rail.scrollLeft / step : 0;
  };

  Carousel.prototype.goTo = function (index) {
    var step = this.step();
    if (!step) return;
    /* Scroll to whichever copy of that colour is nearest, so a tap never runs the rail
       the length of three line-ups. */
    var at = this.progress();
    var base = index % this.n;
    var best = base;
    for (var s = 0; s < SETS; s++) {
      var cand = base + s * this.n;
      if (Math.abs(cand - at) < Math.abs(best - at)) best = cand;
    }
    this.rail.scrollTo({ left: step * best, behavior: 'smooth' });
  };

  /* Once the rail is still, put it back in the middle copy. The copies are identical, so
     the jump is invisible -- and it is what makes the loop endless. */
  Carousel.prototype.rebase = function () {
    if (this.mode !== 'swipe') return;
    var step = this.step();
    if (!step) return;
    var here = Math.round(this.progress());
    if (here < this.n) this.rail.scrollLeft += step * this.n;
    else if (here >= this.n * 2) this.rail.scrollLeft -= step * this.n;
  };

  /* ---- painting --------------------------------------------------------- */

  Carousel.prototype.render = function () {
    if (this.mode !== 'swipe') return;

    var at = this.progress();

    for (var i = 0; i < this.slides.length; i++) {
      var away = Math.min(Math.abs(i - at), 1);
      this.slides[i].style.setProperty('--hooman-bed-scale', String(1 - (1 - MIN_SCALE) * away));
    }

    if (this.track && this.dots.length > 1) {
      var gap = this.dots[1].offsetLeft - this.dots[0].offsetLeft;
      var first = this.dots[0].offsetLeft + this.dots[0].offsetWidth / 2;
      var middle = this.dotWrap.clientWidth / 2;
      this.track.style.setProperty('--hooman-bed-dots', middle - (first + gap * at) + 'px');
    }

    var now = Math.max(0, Math.min(this.slides.length - 1, Math.round(at)));
    if (now === this.active) return;
    this.active = now;

    var real = this.n + (now % this.n);
    for (var d = 0; d < this.dots.length; d++) {
      this.dots[d].classList.toggle('is-centred', d === now);
      /* The ring follows whichever copy is centred; the state a screen reader reads
         stays on the one real dot for that colour. */
      this.dots[d].setAttribute('aria-current', d === real ? 'true' : 'false');
    }

    var label = this.labels[now % this.n];
    if (this.name && this.name.textContent !== label) {
      var el = this.name;
      el.setAttribute('data-fading', '1');
      setTimeout(function () {
        el.textContent = label;
        el.removeAttribute('data-fading');
      }, 120);
    }
  };

  Carousel.prototype.schedule = function () {
    if (this.ticking) return;
    this.ticking = true;
    var self = this;
    requestAnimationFrame(function () {
      self.ticking = false;
      self.render();
    });
  };

  Carousel.prototype.bind = function () {
    var self = this;

    this.rail.addEventListener('scroll', function () {
      self.schedule();
      clearTimeout(self.timer);
      self.timer = setTimeout(function () {
        self.rebase();
      }, SETTLE);
    }, { passive: true });

    var again = function () {
      self.layout();
      self.active = -1;
      self.schedule();
    };
    window.addEventListener('resize', again);
    var mq = window.matchMedia(MOBILE);
    if (mq.addEventListener) mq.addEventListener('change', again);
    else if (mq.addListener) mq.addListener(again);

    /* The photographs are lazy-loaded, so the rail's geometry is not final at first paint. */
    var imgs = this.rail.querySelectorAll('img');
    for (var n = 0; n < imgs.length; n++) {
      imgs[n].addEventListener('load', function () {
        self.active = -1;
        self.schedule();
      });
    }
  };

  function start() {
    /* Every bed line-up, not just the ones that become carousels: the single-bed band
       wants its dot moved under the photograph too. */
    var rails = document.querySelectorAll('.hooman-bed__lineup');
    for (var i = 0; i < rails.length; i++) {
      if (rails[i].dataset.hoomanCarousel) continue;
      rails[i].dataset.hoomanCarousel = '1';
      new Carousel(rails[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
  /* The theme editor re-renders a section in place when a setting changes. */
  document.addEventListener('shopify:section:load', start);
})();
